import { Component, OnInit, ElementRef, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { Overlay, OverlayConfig, ConnectionPositionPair} from '@angular/cdk/overlay';
import * as settings from '../common/settings';
import * as utils from '../common/utils';
import { mainMapMngr } from '../asp/MapMngr';
import { MapBarComponent } from './map-bar.component';
import { SubAreaService } from '../sub-area/sub-area.service';
import { FreeDrawBarComponent } from './free-draw-bar.component';
import { OverlayPanelsService, OverlayPanelInfo } from '../common/overlay-panels.service';
import { JumpToComponent } from '../jump-to/jump-to.component';
import { BullsEyeService } from '../bulls-eye/bulls-eye.service';
//
// Tool Bar
//
@Component({
  selector: 'app-tool-bar',
  templateUrl: './tool-bar.component.html',
  styleUrls: ['./tool-bar.component.scss']
})
export class ToolBarComponent implements OnInit {
  public expanded: boolean = true;
  
  private mapBar: OverlayPanelInfo;
  private freeDrawBar: OverlayPanelInfo;

  private inSecondaryMapSelection = false;

  @ViewChild('mapButton', { read: ElementRef }) mapButton: ElementRef;
  @ViewChild('freeDrawButton', { read: ElementRef }) freeDrawButton: ElementRef;
  @ViewChild('jumpToButton', { read: ElementRef }) jumpToButton: ElementRef;
  @ViewChild('homeContextMenu', { read: MatMenu }) homeContextMenu: MatMenu;
  @ViewChildren(MatMenuTrigger) menuTriggers: QueryList<MatMenuTrigger>;

  constructor(private dialog: MatDialog,
              private overlay: Overlay,
              private overlayPanelsSvc: OverlayPanelsService,
              private subAreaSvc: SubAreaService,
              private bullsEyeSvc: BullsEyeService) {
  }

  ngOnInit() {
    setTimeout(() => {
      this.createMapPanel();
      this.createFreeDrawPanel();
    });
  }

  private horizFloatBarPositionStrategy(relativeToButton: ElementRef) {
    let positions = [
      new ConnectionPositionPair(
        { originX: "end", originY: "top" },
        { overlayX: "start", overlayY: "top" },
        5, // offsetX
        0  // offsetY
      )
    ];
    return this.overlay.position().flexibleConnectedTo(relativeToButton).withPositions(positions);
  }

  private createMapPanel() {
    // Create and configure map panel overlay
    let config = new OverlayConfig({ positionStrategy: this.horizFloatBarPositionStrategy(this.mapButton) });
    this.mapBar = this.overlayPanelsSvc.createPanel(MapBarComponent, config);
  }

  private createFreeDrawPanel() {
    // Create and configure free drawing panel overlay
    let config = new OverlayConfig({ positionStrategy: this.horizFloatBarPositionStrategy(this.freeDrawButton) });
    this.freeDrawBar = this.overlayPanelsSvc.createPanel(FreeDrawBarComponent, config);
  }

  private hideFloatingBars() {
    let mapBar = this.mapBar.componentRef.instance;
    let freeDrawBar = this.freeDrawBar.componentRef.instance;
    if (mapBar.isVisible())
      mapBar.toggleVisible();
    if (freeDrawBar.isVisible())
      freeDrawBar.toggleVisible();
  }

  public onExpandClick() {
    this.expanded = !this.expanded;
    if (!this.expanded)
      this.hideFloatingBars();
  }

  public onMapClick() {
    let mapBar = this.mapBar.componentRef.instance;
    if (!mapBar.isVisible()) {
      this.hideFloatingBars();
      this.mapBar.overlayRef.updatePositionStrategy(this.horizFloatBarPositionStrategy(this.mapButton));
      this.mapBar.overlayRef.updatePosition();
    }
    mapBar.toggleVisible();
  }

  public onFreeDrawClick() {
    let freeDrawBar = this.freeDrawBar.componentRef.instance;
    if (!freeDrawBar.isVisible()) {
      this.hideFloatingBars();
      this.freeDrawBar.overlayRef.updatePositionStrategy(this.horizFloatBarPositionStrategy(this.freeDrawButton));
      this.freeDrawBar.overlayRef.updatePosition();
    }
    freeDrawBar.toggleVisible();
  }

  public dragStarted(event) {
    this.hideFloatingBars();
  }

  public dragEnded(event) {
    let pos = utils.getCdkOverlayPosition(event.source.element);
    settings.userSettings.setToolBarPosition(pos.x, pos.y);
  }

  public onHomeClick() {
    this.hideFloatingBars();
    if (settings.userSettings.mapHome.xCenter !== undefined && settings.userSettings.mapHome.yCenter !== undefined)
      mainMapMngr.setMapExtent(settings.userSettings.mapHome, undefined);
  }

  public onHomeContextMenu(event) {
    this.hideFloatingBars();
    this.menuTriggers.toArray()[0].openMenu();
    return false;
  }

  public onHomeSet() {
    let extent = mainMapMngr.getMapExtent();
    settings.userSettings.setMapHome(extent);
  }

  public onRulerClick() {
    this.hideFloatingBars();
    if (this.isRulerActive())
      mainMapMngr.activateDefaultTool();
    else
      mainMapMngr.activateSpecialMeasurmentsTool();
  }

  public isRulerActive(): boolean {
    return mainMapMngr.specialMeasurmentsToolActive();
  }

  public isRulerDisabled(): boolean {
    return !mainMapMngr.defaultToolActive() && !this.isRulerActive();
  }

  public onBullsEyeClick() {
    this.hideFloatingBars();
    this.bullsEyeSvc.toggleActive();
  }

  public isBullsEyeActive(): boolean {
    return this.bullsEyeSvc.isActive();
  }

  public isBullsEyeDisabled(): boolean {
    return !mainMapMngr.defaultToolActive() && !this.isBullsEyeActive();
  }

  public onSecondaryMapClick() {
    this.hideFloatingBars();
    mainMapMngr.activateCreateTool(NeWMI.Geometry.Enums.EGeometryType.Rectangle, this.onCreateToolFinish.bind(this));
    this.inSecondaryMapSelection = true;
  }

  private onCreateToolFinish(this: ToolBarComponent, geometry: any) {
    this.inSecondaryMapSelection = false;

    let extent = new settings.MapExtent(geometry.xCenter, geometry.yCenter, geometry.width, geometry.height);
    this.subAreaSvc.createSubArea(false /* Is 3D */, extent);
  }

  public isSecondaryMapDisabled(): boolean {
    return !mainMapMngr.defaultToolActive() || !this.subAreaSvc.canCreateSubArea(false /* Is 3D */) || this.inSecondaryMapSelection;
  }

  onJumpToClick() {
    let rect = this.jumpToButton.nativeElement.getBoundingClientRect();
    this.dialog.open(JumpToComponent, JumpToComponent.createDialogConfig(rect.top, rect.right));
  }

  isJumpToActive() {
    let dialogRef = this.dialog.getDialogById(JumpToComponent.DIALOG_ID);
    return dialogRef !== undefined;
  }

}
